import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EndpointService } from '@app/core';
import { Microproceso, Tramite, TramiteDefaultAdmin, FormaListasChequeo } from '@app/shared';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

@Injectable()
export class ProcessAutomationService {
  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private api: EndpointService
  ) { }

  /**
   * Consulta un tramite por default.
   *
   * @param {number} idTramite
   * @returns {Promise<Tramite>}
   * @memberof ProcessAutomationService
   */
  getDefaultTramite(idTramite: number): Promise<Tramite> {
    return new Promise((resolve, reject) => {
      this.http.get<Tramite>(this.api.get('adminDefaultWorkflowTramite', [idTramite])).subscribe(
        res => {
          resolve(res);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta el flujo de un proceso secondary
   *
   * @param {number} idTramite
   * @returns {Promise<Tramite>}
   * @memberof ProcessAutomationService
   */
  getDefaultSecondaryProcess(idSecondary: number): Promise<Tramite> {
    return new Promise((resolve, reject) => {
      this.http.get<Tramite>(this.api.get('adminSecondaryWorkflow', [idSecondary])).subscribe(
        res => {
          resolve(res);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta un microproceso por default.
   *
   * @param {number} idTask
   * @returns {Promise<Microproceso>}
   * @memberof ProcessAutomationService
   */
  getDefaultMicroProces(idTask: number): Promise<Microproceso> {
    return new Promise((resolve, reject) => {
      this.http.get<Microproceso>(this.api.get('defaultMicroproces', [idTask])).subscribe(
        res => {
          resolve(res);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta un microproceso por default de una secundaria.
   *
   * @param {number} idTask
   * @returns {Promise<Microproceso>}
   * @memberof ProcessAutomationService
   */
  getDefaultSecondaryMicroProces(idTask: number): Promise<Microproceso> {
    return new Promise((resolve, reject) => {
      this.http.get<Microproceso>(this.api.get('adminSecondaryMicroprocessWorkflow', [idTask])).subscribe(
        res => {
          resolve(res);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Registra un flujo completo en la base de datos.
   *
   * @param {TramiteDefaultAdmin} flow
   * @returns {Promise<any>}
   * @memberof ProcessAutomationService
   */
  newDefaultWorkflowTramite(flow: TramiteDefaultAdmin): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler().post(this.api.get('adminDefaultWorkflowTramite'), flow, {
        observe: 'response'
      }).subscribe(
        res => {
          if (res.status === 201) {
            this.toastr.success(`Flujo sincronizado correctamente.`, 'Operación exitosa');
            resolve(true);
          }
          resolve(false);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error(`No se pudó sincronizar el flujo.`, 'Operación fallida');
          }
          reject(err);
        }
      );
    });
  }

  /**
   * Registra un flujo de una secundaria completo en la base de datos.
   *
   * @param {TramiteDefaultAdmin} flow
   * @returns {Promise<any>}
   * @memberof ProcessAutomationService
   */
  newDefaultWorkflowSecondaryProcess(flow: TramiteDefaultAdmin): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const formData = {
        secundaria: flow.tramite,
        microproces: flow.microproces
      };
      this.http.skipErrorHandler().post(this.api.get('adminSecondaryWorkflow'), formData, {
        observe: 'response'
      }).subscribe(
        res => {
          if (res.status === 201 || res.status === 200) {
            this.toastr.success(`Flujo sincronizado correctamente.`, 'Operación exitosa');
            resolve(true);
          }
          resolve(false);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error(`No se pudó sincronizar el flujo.`, 'Operación fallida');
          }
          reject(err);
        }
      );
    });
  }

  /**
   * Actualiza un flujo completo en la base de datos.
   *
   * @returns {Promise<any>}
   * @memberof ProcessAutomationService
   */
  updateDefaultWorkflowTramite(id: number, actions: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler().put(this.api.get('adminDefaultWorkflowTramite', [id]), actions, {
        observe: 'response'
      }).subscribe(
        res => {
          if (res.status === 201) {
            this.toastr.success(`Flujo sincronizado correctamente.`, 'Operación exitosa');
            resolve(true);
          }
          resolve(false);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error(`No se pudó sincronizar el flujo.`, 'Operación fallida');
          }
          resolve(false);
          reject(err);
        }
      );
    });
  }

  /**
   * Actualiza un flujo de una secundaria completo en la base de datos.
   *
   * @returns {Promise<any>}
   * @memberof ProcessAutomationService
   */
  updateDefaultWorkflowSecondaryProcess(id: number, actions: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler().put(this.api.get('adminSecondaryWorkflow', [id]), actions, {
        observe: 'response'
      }).subscribe(
        res => {
          if (res.status === 201 || res.status === 200) {
            this.toastr.success(`Flujo sincronizado correctamente.`, 'Operación exitosa');
            resolve(true);
          }
          resolve(false);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            this.toastr.error(`No se pudó sincronizar el flujo.`, 'Operación fallida');
          }
          resolve(false);
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta las listas de chequeo de una forma en especifico
   *
   * @param {number} idForm
   * @returns {(Observable<FormaListasChequeo>)}
   * @memberof ProcessAutomationService
   */
  getFormCheckList(idForm: number): Observable<FormaListasChequeo> {
    return new Observable(obs => {
      this.http.get<FormaListasChequeo>(this.api.get('defaultDynamicTaskForm', [idForm])).subscribe(
        form => {
          obs.next(form);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/shared/process-automation.service.ts